import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, BehaviorSubject } from 'rxjs';
import { Router } from '@angular/router';
import { environment } from '../../../environments/environment';
import { tap } from 'rxjs/operators';

const httpOptions = {
  headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
};

export interface User {
  id: number;
  email: string;
  accountType?: string;
  firstName?: string;
  lastName?: string;
  company?: string;
  country?: string;
  zipCode?: string;
  city?: string;
  street?: string;
  phone?: string;
  mobileNumber?: string;
  role: string;
  active: boolean;
}

export interface AuthResponse {
  message: string;
  status: number;
  token?: string;
  user?: User;
}

export interface JwtPayload {
  sub?: string;
  email?: string;
  role?: string;
  exp?: number;
  iat?: number;
  [key: string]: any;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private currentUserSubject = new BehaviorSubject<User | null>(null);
  public currentUser$ = this.currentUserSubject.asObservable();

  constructor(
    private http: HttpClient,
    private router: Router
  ) {
    // Check if user is already logged in
    this.initializeAuthState();
    
    // Start periodic token validation
    this.startTokenValidation();
  }

  private initializeAuthState(): void {
    const token = this.getToken();
    const userStr = localStorage.getItem('clientUser') || sessionStorage.getItem('clientUser');
    
    
    if (token && userStr) {
      try {
        // Try to use stored user first
        const storedUser = JSON.parse(userStr);
        
        // For now, trust the stored token and user without strict validation
        // This prevents logout issues after authentication changes
        this.currentUserSubject.next(storedUser);
        
        // Optional: Still validate token but don't clear storage if invalid
        // Just log the issue for debugging
        if (!this.isTokenValid(token)) {
        }
      } catch (error) {
        // If stored user is invalid, clear corrupted data
        this.clearStorage();
      }
    } else {
    }
  }

  private startTokenValidation(): void {
    // Check token validity and refresh when needed
    this.scheduleTokenRefresh();

    // Listen for storage changes (multi-tab support)
    window.addEventListener('storage', (event) => {
      if (event.key === 'clientToken' || event.key === 'clientUser') {
        this.refreshAuthState();
      }
    });

    // Check if this is a new deployment by looking for a deployment marker
    this.checkDeploymentContext();
  }

  private scheduleTokenRefresh(): void {
    // Check token validity every 2 minutes
    setInterval(() => {
      if (this.isLoggedIn() && this.shouldRefreshToken()) {
        this.performSilentRefresh();
      }
    }, 2 * 60 * 1000); // 2 minutes
  }

  private performSilentRefresh(): void {
    this.refreshToken().subscribe({
      next: () => {
        // Silent refresh successful
      },
      error: () => {
        // Don't logout on a silent refresh failure — the user may just be
        // temporarily offline. The interceptor will handle a real 401 on the
        // next actual API request.
        console.warn('Silent token refresh failed — will retry next cycle');
      }
    });
  }

  private checkDeploymentContext(): void {
    const deploymentMarker = localStorage.getItem('deploymentMarker');
    const currentMarker = this.getDeploymentMarker();
    
    if (deploymentMarker !== currentMarker) {
      
      // Store new deployment marker
      localStorage.setItem('deploymentMarker', currentMarker);
      
      // If we have stored auth data, validate it
      if (this.getToken()) {
        this.refreshAuthState();
      }
    }
  }

  private getDeploymentMarker(): string {
    // Create a marker based on build time or version
    const buildTime = new Date().toISOString().split('T')[0]; // YYYY-MM-DD
    return `deploy-${buildTime}`;
  }

  private isTokenValid(token: string): boolean {
    try {
      const payload = this.decodeToken(token);
      const currentTime = Date.now() / 1000;
      
      // Check if token has expiration and is not expired
      if (payload.exp && payload.exp > currentTime) {
        return true;
      }
      
      // If no expiration, check if token is recent (within 24 hours)
      if (payload.iat && (currentTime - payload.iat) < 86400) {
        return true;
      }
      
      return false;
    } catch (error) {
      return false;
    }
  }

  private clearStorage(): void {
    localStorage.removeItem('clientToken');
    localStorage.removeItem('clientUser');
    sessionStorage.removeItem('clientToken');
    sessionStorage.removeItem('clientUser');
    this.currentUserSubject.next(null);
  }

  register(userData: any): Observable<AuthResponse> {
    const url = `${environment.baseUrl}/auth/register`;
    return this.http.post<AuthResponse>(url, userData, httpOptions);
  }

  login(credentials: { email: string; password: string }): Observable<AuthResponse> {
    const url = `${environment.baseUrl}/auth/client-login`;
    return this.http.post<AuthResponse>(url, credentials, httpOptions);
  }

  logout(): void {
    // Clear both storage types
    localStorage.removeItem('clientToken');
    localStorage.removeItem('clientUser');
    localStorage.removeItem('deploymentMarker');
    
    sessionStorage.removeItem('clientToken');
    sessionStorage.removeItem('clientUser');
    
    this.currentUserSubject.next(null);
    
    
    this.router.navigate(['/auth/login']);
  }

  setSession(authResult: Pick<AuthResponse, 'token' | 'user'>, rememberMe?: boolean): void {
    if (authResult.token && authResult.user) {
      const shouldRemember = rememberMe ?? localStorage.getItem('clientToken') !== null;
      const storage = shouldRemember ? localStorage : sessionStorage;
      const previousStorage = shouldRemember ? sessionStorage : localStorage;

      try {
        storage.setItem('clientToken', authResult.token);
        storage.setItem('clientUser', JSON.stringify(authResult.user));
        previousStorage.removeItem('clientToken');
        previousStorage.removeItem('clientUser');
        this.currentUserSubject.next(authResult.user);
      } catch (error) {
        try {
          storage.removeItem('clientToken');
          storage.removeItem('clientUser');
        } catch (cleanupError) {
          console.error('Failed to clean up an incomplete authentication session', cleanupError);
        }
        console.error('Failed to persist authentication session', error);
        throw new Error('Unable to save your sign-in session. Please check your browser settings and try again.');
      }
    }
  }

  // Test method to verify authentication persistence
  testAuthPersistence(): void {
    const token = this.getToken();
    const user = this.getCurrentUser();
    
    
    // Test localStorage access
    try {
      const testValue = 'test-' + Date.now();
      localStorage.setItem('authTest', testValue);
      const retrievedValue = localStorage.getItem('authTest');
      localStorage.removeItem('authTest');
      
    } catch (error) {
    }
  }

  getCurrentUser(): User | null {
    return this.currentUserSubject.value;
  }

  updateCurrentUser(user: Partial<User>): void {
    const currentUser = this.currentUserSubject.value;
    if (!currentUser) return;

    const updatedUser = { ...currentUser, ...user };
    this.currentUserSubject.next(updatedUser);

    try {
      if (localStorage.getItem('clientUser') !== null) {
        localStorage.setItem('clientUser', JSON.stringify(updatedUser));
      } else if (sessionStorage.getItem('clientUser') !== null) {
        sessionStorage.setItem('clientUser', JSON.stringify(updatedUser));
      }
    } catch (error) {
      console.error('Failed to persist updated profile data', error);
    }
  }

  isLoggedIn(): boolean {
    const token = this.getToken();
    const userStr = localStorage.getItem('clientUser') || sessionStorage.getItem('clientUser');
    
    if (!token) return false;
    
    // If we have a current user, we're logged in
    if (this.currentUserSubject.value) {
      return true;
    }
    
    // Try to restore user from storage
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        this.currentUserSubject.next(user);
        return true;
      } catch (error) {
        // Failed to parse stored user
      }
    }
    
    // If we have a token but no stored user, still consider logged in
    // This handles cases where only the token exists
    return true;
  }

  getToken(): string | null {
    // Try localStorage first, then sessionStorage as fallback
    return localStorage.getItem('clientToken') || sessionStorage.getItem('clientToken');
  }

  // Get current user profile
  getProfile(): Observable<AuthResponse> {
    const url = `${environment.baseUrl}/auth/profile`;
    return this.http.get<AuthResponse>(url, httpOptions);
  }

  // Update user profile
  updateProfile(profileData: any): Observable<AuthResponse> {
    const url = `${environment.baseUrl}/auth/profile`;
    return this.http.patch<AuthResponse>(url, profileData, httpOptions);
  }

  // Request a password-reset link without changing the existing password.
  forgotPassword(email: string): Observable<AuthResponse> {
    const url = `${environment.baseUrl}/auth/forgot-password`;
    return this.http.post<AuthResponse>(url, { email }, httpOptions);
  }

  resetPassword(token: string, password: string): Observable<AuthResponse> {
    const url = `${environment.baseUrl}/auth/reset-password`;
    return this.http.post<AuthResponse>(url, { token, password }, httpOptions);
  }

  confirmEmail(token: string): Observable<AuthResponse> {
    const url = `${environment.baseUrl}/auth/confirm-email`;
    return this.http.post<AuthResponse>(url, { token }, httpOptions);
  }

  confirmEmailChange(token: string): Observable<AuthResponse> {
    const url = `${environment.baseUrl}/auth/confirm-email-change`;
    return this.http.post<AuthResponse>(url, { token }, httpOptions);
  }

  // Resend confirmation email
  resendConfirmationEmail(email: string): Observable<AuthResponse> {
    const url = `${environment.baseUrl}/auth/resend-confirmation`;
    return this.http.post<AuthResponse>(url, { email }, httpOptions);
  }

  private decodeToken(token: string): JwtPayload {
    try {
      const payload = token.split('.')[1];
      const fixedBase64String = payload + '='.repeat((4 - (payload.length % 4)) % 4);
      return JSON.parse(atob(fixedBase64String));
    } catch (error) {
      throw new Error('Invalid token');
    }
  }

  refreshAuthState(): void {
    // Clear current state first
    this.currentUserSubject.next(null);
    
    // Re-initialize from localStorage
    this.initializeAuthState();
  }

  // Method to check if we need to refresh the token
  shouldRefreshToken(): boolean {
    const token = this.getToken();
    if (!token) return false;

    try {
      const payload = this.decodeToken(token);
      const currentTime = Date.now() / 1000;

      // Only refresh if token expires within 5 minutes
      if (payload.exp && (payload.exp - currentTime) < 300) {
        return true;
      }

      // If token has no exp, don't auto-refresh (avoid logout loop)
      return false;
    } catch (error) {
      return false;
    }
  }

  // Method to handle token refresh
  refreshToken(): Observable<AuthResponse> {
    const token = this.getToken();
    const url = `${environment.baseUrl}/auth/refresh`;
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    });
    return this.http.post<AuthResponse>(url, {}, { headers }).pipe(
      tap((response: AuthResponse) => {
        if (response.token && response.user) {
          this.setSession(response);
        }
      })
    );
  }
}
